import React, { useEffect, useMemo } from 'react';
import { EntityNotFound, NewPageWrapper, RichTable, ServerError, useTitle } from '../../common';
import { useParams } from 'react-router-dom';
import { Spinner } from '../../common/components/Spinner/Spinner';
import { ErrorResponse } from '../../common/types/errorRespose';
import { useBreadcrumbsActions } from '../../common/components/Breadcrumbs/context/useBreadcrumbsActions';
import { RefreshButton } from '../../common/components/Breadcrumbs/buttons/RefreshButton/RefreshButton';
import { Flexbox, Typography } from '@hiiretail/synergy-ui';
import { Card } from '../../common/components/Card/Card';
import { TableData } from '@hiiretail/synergy-ui/dist/types/components/surfaces/Table/Table.types';
import { useGetBuiltInRole } from './hooks/useGetBuiltInRole';

export const BuiltInRoleDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { updateActions } = useBreadcrumbsActions();

  const {
    data: role,
    error: fetchError,
    refetch,
    isFetching,
  } = useGetBuiltInRole(id as string, { retry: false, keepPreviousData: false });

  useTitle(role ? `Built-in Roles | ${role.id}` : 'Built-in Roles');

  useEffect(() => {
    updateActions(
      <RefreshButton onClick={refetch} permission="iam.role.list" />,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const permissionsTableData = useMemo(() => (role?.permissions ?? []).map((permId) => ({
    id: permId
  })), [role]);

  const tableColumns = useMemo(() => [{ Header: 'Identifier', accessor: 'id' }], []);

  return (
    <NewPageWrapper>
      {isFetching ? (
        <Spinner dataTestId="built-in-role-details-spinner" />
      ) : (
        <>
          {fetchError && (fetchError as ErrorResponse | null)?.response?.status !== 404 && (
            <ServerError error={(fetchError as ErrorResponse | null)?.message} />
          )}
          {fetchError && (fetchError as ErrorResponse | null)?.response?.status === 404 && (
            <EntityNotFound error="Role not found" itemTitle="Role" />
          )}

          {!fetchError && !!role && (
            <Flexbox container gutter={4} style={{ height: '100%', width: '100%' }}>
              <Flexbox item style={{ width: '25%' }}>
                <Card title={role.id}>
                  {role.name && <Typography>{role.name}</Typography>}
                </Card>
              </Flexbox>

              <Flexbox item style={{ width: '75%', zIndex: 99 }}>
                <RichTable
                  id="built-in-role-permissions"
                  title="Permissions"
                  columns={tableColumns}
                  data={permissionsTableData as unknown as TableData[]}
                  isLoading={isFetching}
                  fetchError={null}
                  errorSubject="permissions"
                  searchFieldLabel="Filter by id"
                  onSearchChange={() => void 0}
                  permissions={{
                    list: 'iam.role.list',
                    create: 'iam.role.create'
                  }}
                  hideSearch
                />
              </Flexbox>
            </Flexbox>
          )}
        </>
      )}
    </NewPageWrapper>
  );
};
